import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
	REMINDER_ID_PARAM,
	REMINDER_SCROLL_PARAM,
	REMINDER_SCROLL_VALUE,
	REMINDERS_SECTION_ANCHOR_ID,
	reminderRowAnchorId,
} from "web_myplann/NotificationCenter/Utils/reminderScrollParam";

// Handles the specific-reminder deep link (?scrollTo=reminders&reminderId=N).
// It runs where the reminders list renders, so `isLoaded` reflects the list
// query itself — the page-level useScrollToReminders can't know when the rows
// exist and defers to this hook whenever reminderId is present.
export const useScrollToReminderRow = (isLoaded: boolean): void => {
	const [searchParams] = useSearchParams();
	const hasScrolledRef = useRef(false);
	const shouldScroll =
		searchParams.get(REMINDER_SCROLL_PARAM) === REMINDER_SCROLL_VALUE;
	const reminderId = searchParams.get(REMINDER_ID_PARAM);

	useEffect(() => {
		if (!shouldScroll || !reminderId || !isLoaded || hasScrolledRef.current) {
			return;
		}
		// The rows are committed by the time this effect runs; a missing row
		// (deleted/solved reminder) falls back to the section anchor.
		const element =
			document.getElementById(reminderRowAnchorId(reminderId)) ??
			document.getElementById(REMINDERS_SECTION_ANCHOR_ID);
		if (!element) {
			return;
		}
		hasScrolledRef.current = true;
		requestAnimationFrame(() => {
			element.scrollIntoView({ behavior: "smooth", block: "start" });
		});
	}, [shouldScroll, reminderId, isLoaded]);
};
